import { useMemo, useState } from "react";
import { useSort, sortGlyph } from "../hooks/useSort";
import { exportRowsAsCsv } from "../exportCsv";
import "../researchMatrixTable.css";

/**
 * The shared interactive research-viz component -- a generic, sortable
 * rows x columns matrix with an optional "highlight + gaps" readout and
 * CSV export, built once for the index-rotation article and meant to be
 * reused by every future living article that needs a real data table
 * (not just this one). Deliberately generic:
 *   - sorting reuses the app-wide useSort hook, same glyph convention
 *     every other table already uses.
 *   - CSV export reuses exportRowsAsCsv, same as every other table.
 *   - highlighting is driven entirely by caller-supplied `highlightPresets`
 *     (a {key,label,test} list) -- the component owns the picker UI, the
 *     caller owns what "highlighted" means for its own data.
 *   - `chartSlot` is an unused-by-this-article extension point for a
 *     future article that wants a chart alongside the table -- no
 *     generic chart engine is built here; a future caller supplies its
 *     own render function.
 *
 * TWO highlight modes, mutually exclusive (pass one or the other):
 *   - `highlightPresets`/`highlightableColumns` (COLUMN mode): pick a
 *     column, highlight matching cells down every row.
 *   - `rowHighlight` (ROW mode): pick a row, highlight matching cells
 *     along THAT row's own columns, with gaps measured in column
 *     POSITIONS -- correct regardless of column display order (newest-
 *     first or oldest-first), since position differences in a uniform
 *     weekly grid equal week differences either way.
 *
 * VIRTUALIZED by construction: only rows inside the current scroll
 * window (+ overscan) are rendered as real <tr> elements -- two spacer
 * rows absorb the rest of the table's height, so the DOM stays small
 * regardless of how many weeks accumulate. Sorting and CSV export always
 * operate on the FULL row set, never just the visible window.
 */

export interface MatrixColumn {
  key: string;
  label: string;
}

export interface HighlightPreset {
  key: string;
  label: string;
  test: (value: unknown) => boolean;
}

export interface RowHighlightConfig {
  presets: HighlightPreset[];
  /** Picker options: value = that row's own rowKey() output, label = display text. */
  rowOptions: { key: string; label: string }[];
  /** Show the "gaps between highlighted columns, for the selected row"
   * readout once a row + preset are both selected. */
  gapsReadoutEnabled?: boolean;
}

export interface ResearchMatrixTableProps {
  /** Row-oriented data. The FIRST entry is treated as the row-label column
   * (e.g. "week") for CSV/export headers; order is assumed chronological
   * for the COLUMN-mode gaps readout (sorting only affects what's
   * rendered, not the gap computation, since "gap between hits" is
   * inherently time-based). */
  rows: Record<string, unknown>[];
  columns: MatrixColumn[];
  rowKey: (row: Record<string, unknown>) => string;
  /** Columns the reader can pick to highlight (defaults to every column
   * after the first, i.e. every data column, not the row-label column).
   * COLUMN mode only. */
  highlightableColumns?: string[];
  highlightPresets?: HighlightPreset[];
  /** Show the "gaps between highlighted rows" readout once a column +
   * preset are both selected (COLUMN mode). Off by default -- only
   * meaningful when rows are a real chronological sequence. */
  gapsReadoutEnabled?: boolean;
  /** ROW-mode highlighting -- see the module doc above. Mutually
   * exclusive with highlightPresets/highlightableColumns. */
  rowHighlight?: RowHighlightConfig;
  exportFilename: string;
  chartSlot?: (rows: Record<string, unknown>[]) => React.ReactNode;
  cellFormat?: (value: unknown, columnKey: string) => string;
}

const ROW_HEIGHT_PX = 30;
const VISIBLE_ROWS = 16;
const OVERSCAN = 6;

function defaultCellFormat(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(1);
  return String(value);
}

export function ResearchMatrixTable({
  rows, columns, rowKey, highlightableColumns, highlightPresets, gapsReadoutEnabled, rowHighlight,
  exportFilename, chartSlot, cellFormat = defaultCellFormat,
}: ResearchMatrixTableProps) {
  const getValue = (row: Record<string, unknown>, key: string) => row[key];
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, getValue, columns[0]?.key ?? null);

  // --- COLUMN mode state ---
  const hlColumns = highlightableColumns ?? columns.slice(1).map((c) => c.key);
  const [hlColumn, setHlColumn] = useState<string>("");
  const [hlPresetKey, setHlPresetKey] = useState<string>(highlightPresets?.[0]?.key ?? "");
  const activePreset = highlightPresets?.find((p) => p.key === hlPresetKey) ?? null;
  const highlightActive = hlColumn !== "" && activePreset !== null;

  // --- ROW mode state ---
  const [hlRow, setHlRow] = useState<string>("");
  const [hlRowPresetKey, setHlRowPresetKey] = useState<string>(rowHighlight?.presets[0]?.key ?? "");
  const activeRowPreset = rowHighlight?.presets.find((p) => p.key === hlRowPresetKey) ?? null;
  const rowHighlightActive = hlRow !== "" && activeRowPreset !== null;
  const selectedRow = useMemo(() => (hlRow ? rows.find((r) => rowKey(r) === hlRow) ?? null : null), [rows, hlRow, rowKey]);

  const [scrollTop, setScrollTop] = useState(0);
  const viewportHeight = VISIBLE_ROWS * ROW_HEIGHT_PX;
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT_PX) - OVERSCAN);
  const endIdx = Math.min(sorted.length, Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT_PX) + OVERSCAN);
  const visible = sorted.slice(startIdx, endIdx);
  const topSpacer = startIdx * ROW_HEIGHT_PX;
  const bottomSpacer = (sorted.length - endIdx) * ROW_HEIGHT_PX;

  const gapsInfo = useMemo(() => {
    if (!gapsReadoutEnabled || !highlightActive) return null;
    const hits: { idx: number; row: Record<string, unknown> }[] = [];
    rows.forEach((r, i) => {
      if (activePreset!.test(r[hlColumn])) hits.push({ idx: i, row: r });
    });
    const gaps = hits.slice(1).map((h, i) => h.idx - hits[i].idx);
    const meanGap = gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : null;
    return { hits, gaps, meanGap };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, hlColumn, hlPresetKey, gapsReadoutEnabled, highlightActive]);

  // ROW-mode gaps: walk the COLUMN array (excluding the row-label column)
  // in its given order and test the SELECTED ROW's own value in each --
  // position differences equal week differences in this uniform weekly
  // grid regardless of whether columns display newest-first or oldest-
  // first, so this is correct either way without needing real dates.
  const rowGapsInfo = useMemo(() => {
    if (!rowHighlight?.gapsReadoutEnabled || !rowHighlightActive || !selectedRow) return null;
    const dataCols = columns.slice(1);
    const hits: { idx: number; col: MatrixColumn }[] = [];
    dataCols.forEach((c, i) => {
      if (activeRowPreset!.test(selectedRow[c.key])) hits.push({ idx: i, col: c });
    });
    const gaps = hits.slice(1).map((h, i) => h.idx - hits[i].idx);
    const meanGap = gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : null;
    return { hits, gaps, meanGap };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, selectedRow, hlRowPresetKey, rowHighlight?.gapsReadoutEnabled, rowHighlightActive]);

  return (
    <div className="rmt-wrap">
      <div className="rmt-toolbar">
        {highlightPresets && highlightPresets.length > 0 ? (
          <>
            <label className="rmt-label">
              Highlight
              <select value={hlColumn} onChange={(e) => setHlColumn(e.target.value)}>
                <option value="">— pick a column —</option>
                {hlColumns.map((k) => {
                  const col = columns.find((c) => c.key === k);
                  return (
                    <option key={k} value={k}>
                      {col?.label ?? k}
                    </option>
                  );
                })}
              </select>
            </label>
            <div className="rmt-presets">
              {highlightPresets.map((p) => (
                <button
                  key={p.key}
                  className={`rmt-preset-btn ${hlPresetKey === p.key ? "active" : ""}`}
                  onClick={() => setHlPresetKey(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </>
        ) : null}
        {rowHighlight && rowHighlight.presets.length > 0 ? (
          <>
            <label className="rmt-label">
              Highlight
              <select value={hlRow} onChange={(e) => setHlRow(e.target.value)}>
                <option value="">— pick a sector —</option>
                {rowHighlight.rowOptions.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="rmt-presets">
              {rowHighlight.presets.map((p) => (
                <button
                  key={p.key}
                  className={`rmt-preset-btn ${hlRowPresetKey === p.key ? "active" : ""}`}
                  onClick={() => setHlRowPresetKey(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </>
        ) : null}
        <span className="rmt-spacer" />
        <button className="rmt-csv" onClick={() => exportRowsAsCsv(sorted, columns, exportFilename)}>
          ↓ CSV
        </button>
        <span className="rmt-count">{rows.length} rows</span>
      </div>

      <div className="rmt-scroll" style={{ maxHeight: viewportHeight }} onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}>
        <table className="rmt-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  className={highlightActive && c.key === hlColumn ? "rmt-hl-col" : undefined}
                >
                  {c.label} <span className="rmt-sortglyph">{sortGlyph(c.key, sortKey, sortDir)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr aria-hidden="true" style={{ height: topSpacer }}>
              <td colSpan={columns.length} style={{ padding: 0, border: 0 }} />
            </tr>
            {visible.map((row) => {
              const isHlRow = rowHighlightActive && rowKey(row) === hlRow;
              return (
                <tr key={rowKey(row)} className={isHlRow ? "rmt-hl-row" : undefined}>
                  {columns.map((c, ci) => {
                    const v = row[c.key];
                    const hlCol = highlightActive && c.key === hlColumn && activePreset!.test(v);
                    const hlRowCell = isHlRow && ci > 0 && activeRowPreset!.test(v);
                    const cls = [hlCol ? "rmt-hl-cell" : "", hlRowCell ? "rmt-hl-cell" : ""].filter(Boolean).join(" ") || undefined;
                    return (
                      <td key={c.key} className={cls}>
                        {cellFormat(v, c.key)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr aria-hidden="true" style={{ height: bottomSpacer }}>
              <td colSpan={columns.length} style={{ padding: 0, border: 0 }} />
            </tr>
          </tbody>
        </table>
      </div>

      {gapsInfo && gapsInfo.hits.length > 0 ? (
        <div className="rmt-gaps">
          <div className="rmt-gaps-label">
            {columns.find((c) => c.key === hlColumn)?.label} — {activePreset!.label}: {gapsInfo.hits.length} week(s)
          </div>
          <div className="rmt-gaps-list">
            {gapsInfo.hits.map((h, i) => (
              <span key={h.idx} className="rmt-gaps-chip">
                {String(h.row[columns[0].key])}
                {i < gapsInfo.gaps.length ? ` → +${gapsInfo.gaps[i]}wk` : ""}
              </span>
            ))}
          </div>
          {gapsInfo.gaps.length > 0 ? (
            <div className="rmt-gaps-stat">
              gap sequence (weeks): {gapsInfo.gaps.join(", ")} — mean {gapsInfo.meanGap!.toFixed(1)} weeks
            </div>
          ) : (
            <div className="rmt-gaps-stat">Only one hit so far — no gap to measure yet.</div>
          )}
        </div>
      ) : highlightActive ? (
        <div className="rmt-gaps rmt-gaps-empty">No weeks match this column + preset.</div>
      ) : null}

      {rowGapsInfo && rowGapsInfo.hits.length > 0 ? (
        <div className="rmt-gaps">
          <div className="rmt-gaps-label">
            {rowHighlight!.rowOptions.find((o) => o.key === hlRow)?.label} — {activeRowPreset!.label}: {rowGapsInfo.hits.length} week(s)
          </div>
          <div className="rmt-gaps-list">
            {rowGapsInfo.hits.map((h, i) => (
              <span key={h.idx} className="rmt-gaps-chip">
                {h.col.label}
                {i < rowGapsInfo.gaps.length ? ` → +${rowGapsInfo.gaps[i]}wk` : ""}
              </span>
            ))}
          </div>
          {rowGapsInfo.gaps.length > 0 ? (
            <div className="rmt-gaps-stat">
              gap sequence (weeks): {rowGapsInfo.gaps.join(", ")} — mean {rowGapsInfo.meanGap!.toFixed(1)} weeks
            </div>
          ) : (
            <div className="rmt-gaps-stat">Only one hit so far — no gap to measure yet.</div>
          )}
        </div>
      ) : rowHighlightActive ? (
        <div className="rmt-gaps rmt-gaps-empty">No weeks match this sector + preset.</div>
      ) : null}

      {chartSlot ? chartSlot(sorted) : null}
    </div>
  );
}
